import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Badge,
} from "@chakra-ui/react";
import { fetchWithAuth } from "../utils/fetchWithAuth";

const AddViewSelector = ({ enabledFeatures, onFeatureSelect, onClose }) => {
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAvailableFeatures = async () => {
      try {
        setLoading(true);
        const enabledFeaturesParam = enabledFeatures?.join(',') || '';
        const response = await fetchWithAuth(
          `/api/core-features-registry/available-features?enabledFeatures=${encodeURIComponent(enabledFeaturesParam)}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setAvailableFeatures(data.availableFeatures || []);
        } else {
          setError("Failed to load available features");
        }
      } catch (err) {
        console.error("Error fetching available features:", err);
        setError("Failed to load available features");
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableFeatures();
  }, [enabledFeatures]);

  const handleFeatureClick = (feature) => {
    if (feature.dependenciesMet) {
      onFeatureSelect(feature.featureId);
    }
  };

  const getDependencyLabel = (dependencyMessage) => {
    if (!dependencyMessage) return null;
    const match = dependencyMessage.match(/Requires (.+)/);
    return match ? match[1] : dependencyMessage;
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minH="200px"
      >
        <Spinner size="md" color="gray.400" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minH="200px"
      >
        <Text color="red.500" fontSize="sm">{error}</Text>
      </Box>
    );
  }

  if (availableFeatures.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minH="200px"
      >
        <VStack spacing={2}>
          <span className="material-icons-outlined" style={{ fontSize: "32px", color: "#718096" }}>
            check_circle
          </span>
          <Text color="gray.500" fontSize="sm">All views are enabled</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box p={6} maxW="600px" mx="auto">
      <VStack spacing={1} align="stretch">
        {availableFeatures.map((feature) => (
          <HStack
            key={feature.featureId}
            py={3}
            px={4}
            spacing={4}
            borderRadius="lg"
            cursor={feature.dependenciesMet ? "pointer" : "not-allowed"}
            bg="transparent"
            border="1px solid transparent"
            alignItems="flex-start"
            _hover={feature.dependenciesMet ? {
              bg: "gray.50",
              border: "1px solid",
              borderColor: "gray.200",
              transform: "translateX(4px)",
              boxShadow: "sm",
            } : {
              bg: "gray.25",
            }}
            transition="all 0.2s ease"
            onClick={() => handleFeatureClick(feature)}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="36px"
              h="36px"
              borderRadius="md"
              bg="gray.100"
              flexShrink={0}
              mt={0.5}
            >
              <span 
                className="material-icons-outlined" 
                style={{ 
                  fontSize: "20px", 
                  color: "#1A202C"
                }}
              >
                {feature.icon}
              </span>
            </Box>
            
            <VStack align="start" spacing={1} flex={1}>
              <HStack spacing={2} flexWrap="wrap">
                <Text 
                  fontWeight="500" 
                  fontSize="sm"
                  color="gray.800"
                >
                  {feature.label}
                </Text>
                {!feature.dependenciesMet && feature.dependencyMessage && (
                  <Badge 
                    size="sm" 
                    colorScheme="gray"
                    variant="subtle"
                    fontSize="10px"
                    fontWeight="500"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <span 
                      className="material-icons-outlined" 
                      style={{ fontSize: "10px" }}
                    >
                      lock
                    </span>
                    Needs {getDependencyLabel(feature.dependencyMessage)}
                  </Badge>
                )}
              </HStack>
              <Text 
                fontSize="xs" 
                color="gray.500"
                lineHeight="1.4"
              >
                {feature.description}
              </Text>
            </VStack>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
};

export default AddViewSelector;
